/**
 * O erro RFC 9457 vira **tipo da aplicacao** — com o codigo do catalogo fechado e o texto que
 * o proprio catalogo ja define (`API §2.1`, `§2.2`; `packages/contracts/src/errors.ts`).
 *
 * A regra que este arquivo existe para tornar mecanica: **a tela escolhe como mostrar, nunca
 * inventa o texto**. Por isso nao ha uma unica frase em portugues aqui dentro — todas vem de
 * `ERROR_CATALOG` ou do `detail` que o servidor mandou.
 *
 * ## Por que dois tipos, e nao um
 *
 * | Tipo                | Quando                                                                          | O que a tela sabe |
 * | ------------------- | ------------------------------------------------------------------------------- | ----------------- |
 * | `ApiError`          | O servidor respondeu `problem+json` com codigo do catalogo                       | Tudo: codigo, acao sugerida, campos, `meta` |
 * | `ApiTransportError` | Nao houve resposta conforme: rede caiu, borda devolveu HTML, requisicao abortada | So que nao deu para falar com a API |
 *
 * Colapsar os dois num tipo so exigiria inventar um codigo de catalogo para "a borda devolveu
 * uma pagina de erro" — e o catalogo esta **fechado em 106**. `ApiTransportError` carrega um
 * `fallbackCode` do catalogo para que a tela ainda tenha um texto pt-BR legitimo para exibir,
 * sem que ninguem finja que o servidor mandou aquele codigo.
 */
import {
  ERROR_CATALOG,
  ProblemDetailsSchema,
  isErrorCode,
  type CartResponse,
  type ClientAction,
  type ErrorCode,
  type ProblemDetails,
  type ProblemFieldError,
} from '@farmacia/contracts';

/** Raiz comum: um `catch` so pega os dois. */
export abstract class ApiClientError extends Error {
  abstract readonly kind: 'API' | 'TRANSPORTE';

  /**
   * O texto pt-BR que a tela pode exibir. **Sempre** vem do catalogo ou do servidor.
   * `null` nas linhas em que o catalogo escreve "—": erro de integracao que nao tem usuario.
   */
  abstract readonly userMessage: string | null;
}

export interface ApiErrorInput {
  readonly problem: ProblemDetails;
  readonly status: number;
  readonly method: string;
  readonly path: string;
}

/**
 * Um erro do catalogo fechado.
 *
 * `message` (o do `Error`) e para o log e para o Sentry — leva codigo, metodo e caminho. O que
 * a pessoa le e `userMessage`.
 */
export class ApiError extends ApiClientError {
  override readonly name: string = 'ApiError';
  readonly kind = 'API' as const;

  /** A **unica** coisa contra a qual a tela programa (`API §2.1`). */
  readonly code: ErrorCode;
  readonly status: number;
  /** Coluna "Acao no cliente" do `API §2.2`: `RETRY`, `REFRESH`, `BLOCK`, `FIELD`, `AUTH`, ... */
  readonly action: ClientAction;
  /** A mensagem que o catalogo redige para este codigo. Pode conter `{n}`, `{loja}`, ... */
  readonly catalogMessage: string | null;
  /** O `detail` do servidor, ja com os placeholders preenchidos, quando ele o manda. */
  readonly detail: string | undefined;
  readonly requestId: string;
  /** Erros de campo — so em `422` de validacao (`API §2.1`). */
  readonly fieldErrors: readonly ProblemFieldError[];
  /** Dados de recuperacao: `current` do conflito, `missingKey`/`owner` do perfil, ... */
  readonly meta: Readonly<Record<string, unknown>>;
  readonly problem: ProblemDetails;

  constructor(input: ApiErrorInput) {
    const entry = ERROR_CATALOG[input.problem.code];
    super(`${input.problem.code} (${String(input.status)}) em ${input.method} ${input.path}`);

    this.code = input.problem.code;
    this.status = input.status;
    this.action = entry.acaoSugerida;
    this.catalogMessage = entry.mensagemUsuario;
    this.detail = input.problem.detail;
    this.requestId = input.problem.requestId;
    this.fieldErrors = input.problem.errors ?? [];
    this.meta = input.problem.meta ?? {};
    this.problem = input.problem;
  }

  /**
   * O texto a exibir: o `detail` do servidor quando existe, senao a linha do catalogo.
   *
   * A ordem tem motivo, e nao e preferencia. Varias linhas do catalogo trazem placeholder
   * (`Ajustamos a quantidade: temos {n} unidades.`); quem os preenche e a camada HTTP, e o
   * resultado chega em `detail`. Mostrar a linha crua do catalogo com `{n}` na tela seria pior
   * do que nao mostrar nada.
   */
  override get userMessage(): string | null {
    return this.detail ?? this.catalogMessage;
  }
}

/**
 * `409 CART_VERSION_CONFLICT` — o `If-Match` perdeu.
 *
 * Existe como subclasse, e nao como um `if (erro.code === ...)` espalhado, por causa do
 * `API §4.2`: o corpo do conflito ja traz **a representacao atual completa** em `meta.current`,
 * *"assim o cliente resolve o conflito sem um segundo GET (importante em 3G)"*. Quem pega este
 * erro tem o carrinho novo na mao — nao precisa recarregar, e **nao deve** reenviar a escrita,
 * que e exatamente como se sobrescreve o trabalho do outro dispositivo.
 */
export class CartVersionConflictError extends ApiError {
  override readonly name: string = 'CartVersionConflictError';

  /**
   * O carrinho como esta agora, ja validado por `CartVersionConflictMetaSchema`. `null` quando
   * nao ha mais carrinho para este dono (sessao trocada, TTL vencido, merge que o aposentou) —
   * e tambem quando o `meta` veio fora do contrato, caso em que o cliente prefere "esqueci a
   * versao" a entregar um objeto meio-preenchido para a tela renderizar.
   */
  readonly current: CartResponse | null;

  constructor(input: ApiErrorInput, current: CartResponse | null) {
    super(input);
    this.current = current;
  }
}

/** Por que nao houve resposta conforme. */
export type TransportFailureKind =
  /** Rede indisponivel, DNS, TLS, `fetch` rejeitado. */
  | 'OFFLINE'
  /** Houve resposta HTTP, mas nao era `problem+json` valido (pagina de erro da borda, HTML). */
  | 'RESPOSTA_NAO_CONFORME'
  /** `AbortSignal` disparado — troca de tela, timeout de quem chamou. */
  | 'ABORTADO';

/**
 * Falha antes do contrato: nao ha codigo do catalogo porque o servidor nao chegou a falar.
 *
 * `fallbackCode` da a tela um texto pt-BR legitimo:
 * - `SERVICE_DEGRADED` para rede e borda;
 * - `INTERNAL_ERROR` para `5xx` sem corpo conforme.
 *
 * **DUVIDA:** o catalogo fechado (`API §2.2`) nao tem linha para "resposta nao-conforme" nem
 * para "sem rede" — sao estados do cliente, nao da API, e nenhum documento os nomeia. Reusar
 * as duas linhas acima e a leitura mais proxima do que existe; criar um codigo 107 seria abrir
 * pelo lado do cliente um catalogo que o `API §10.3` fecha. Reportado.
 */
export class ApiTransportError extends ApiClientError {
  override readonly name: string = 'ApiTransportError';
  readonly kind = 'TRANSPORTE' as const;

  readonly failure: TransportFailureKind;
  /** O status HTTP, quando houve resposta. `null` quando o `fetch` nem completou. */
  readonly status: number | null;
  readonly fallbackCode: ErrorCode;

  constructor(input: {
    readonly failure: TransportFailureKind;
    readonly status: number | null;
    readonly method: string;
    readonly path: string;
    readonly cause?: unknown;
  }) {
    super(`${input.failure} em ${input.method} ${input.path}`);
    this.failure = input.failure;
    this.status = input.status;
    this.fallbackCode =
      input.status !== null && input.status >= 500 ? 'INTERNAL_ERROR' : 'SERVICE_DEGRADED';
    if (input.cause !== undefined) this.cause = input.cause;
  }

  override get userMessage(): string | null {
    return ERROR_CATALOG[this.fallbackCode].mensagemUsuario;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

/** `if (temCodigo(erro, 'QUOTE_EXPIRED', 'PRICE_CHANGED'))` — sem string solta na tela. */
export function temCodigo(error: unknown, ...codes: readonly ErrorCode[]): error is ApiError {
  return isApiError(error) && codes.includes(error.code);
}

/**
 * Le o corpo como problem details.
 *
 * Devolve `null` — e nao lanca — quando o corpo nao e do contrato. Quem chama transforma isso
 * em `ApiTransportError`, que e a verdade do que aconteceu: a borda respondeu, a API nao.
 *
 * O `isErrorCode` antes do `parse` nao e redundancia. `ErrorCodeSchema` ja recusaria o codigo
 * desconhecido, mas a mensagem do Zod ("invalid enum value, expected 106 of...") nao ajuda
 * ninguem: codigo fora do catalogo e **deriva de contrato**, e quem le o log precisa ver isso
 * com essas palavras, nao uma lista de 106 enums.
 */
export function parseProblemDetails(body: unknown): ProblemDetails | null {
  if (typeof body !== 'object' || body === null) return null;
  const code: unknown = (body as { code?: unknown }).code;
  if (typeof code !== 'string' || !isErrorCode(code)) return null;

  const parsed = ProblemDetailsSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}
